// ============================================
// R14 (t46): INVITE-LINK HOOKS
// ============================================
//
// TanStack Query bindings for the multi-link invite system.
// Used by the project-settings page (list / create / revoke)
// and by the public accept page (preview + redeem).

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPost, apiDelete } from '@/lib/api';
import type { ProjectRole } from '@/lib/schemas';

// ── Shape mirrors backend invite-links.ts response. ──────────
export interface ProjectInviteLink {
    id: string;
    token: string;
    projectId: string;
    role: ProjectRole;
    expiresAt: string | null;
    createdAt: string;
    createdBy: { id: string; name: string };
    revokedAt: string | null;
    inviteUrl: string;
}

export interface InviteLinkPreview {
    projectId: string;
    projectName: string;
    projectColor: string | null;
    projectEmoji: string | null;
    role: ProjectRole;
    expiresAt: string | null;
    invitedBy: { id: string; name: string };
}

export interface CreateInviteLinkInput {
    role: ProjectRole;
    expiresAt?: string | null;
}

export interface RedeemInviteLinkResult {
    projectId: string;
    role: ProjectRole;
    alreadyMember: boolean;
}

// ── Query keys ────────────────────────────────────────────────
export const inviteLinkKeys = {
    all: ['invite-links'] as const,
    lists: () => [...inviteLinkKeys.all, 'list'] as const,
    list: (projectId: string) => [...inviteLinkKeys.lists(), projectId] as const,
    preview: (token: string) => [...inviteLinkKeys.all, 'preview', token] as const,
};

// ── Owner / Admin hooks (project-settings page) ───────────────

export function useProjectInviteLinks(projectId: string) {
    return useQuery({
        queryKey: inviteLinkKeys.list(projectId),
        queryFn: async () => {
            const data = await apiGet<{ items: ProjectInviteLink[] }>(
                `/projects/${projectId}/invite-links`
            );
            return data.items;
        },
        enabled: !!projectId,
        staleTime: 30_000,
    });
}

export function useCreateInviteLink(projectId: string) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (input: CreateInviteLinkInput) => {
            return apiPost<ProjectInviteLink, CreateInviteLinkInput>(
                `/projects/${projectId}/invite-links`,
                input
            );
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: inviteLinkKeys.list(projectId) });
        },
    });
}

export function useRevokeInviteLink(projectId: string) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (linkId: string) => {
            return apiDelete<void>(`/projects/${projectId}/invite-links/${linkId}`);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: inviteLinkKeys.list(projectId) });
        },
    });
}

// ── Public hooks (accept page) ────────────────────────────────

export function useInviteLinkPreview(token: string | null | undefined) {
    return useQuery({
        queryKey: inviteLinkKeys.preview(token ?? ''),
        queryFn: async () => {
            return apiGet<InviteLinkPreview>(`/invite-links/${token}/preview`);
        },
        enabled: !!token,
        retry: false,
        staleTime: 60_000,
    });
}

export function useRedeemInviteLink() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (token: string) => {
            return apiPost<RedeemInviteLinkResult, Record<string, never>>(
                `/invite-links/${token}/redeem`,
                {} as Record<string, never>
            );
        },
        onSuccess: () => {
            // Invalidate project lists so the new membership shows.
            queryClient.invalidateQueries({ queryKey: ['projects'] });
        },
    });
}
